import React, { createContext, useContext, useMemo } from 'react';
import { FEATURE_FLAGS, type FeatureFlags, isFeatureEnabled, isFeatureEnabledWithConditions } from '../generated/feature-flags';
import { FeatureFlagAnalytics, FeatureFlagPerformanceMonitor, useFeatureFlagAnalytics } from './feature-flag-analytics';

// Re-export types from generated file
export { FeatureFlagNames } from '../generated/feature-flags';
export type { FeatureFlag, FeatureFlags } from '../generated/feature-flags';

// User context interface for feature flag evaluation
export interface UserContext {
    userId?: string;
    userRole?: string;
    plan?: string;
    region?: string;
    environment?: string;
    env?: string;
    experimentGroup?: string;
    [key: string]: any;
}

// Feature flags context
interface FeatureFlagsContextValue {
    userContext: UserContext;
    setUserContext: (context: UserContext) => void;
    isEnabled: (flagName: keyof FeatureFlags) => boolean;
    isEnabledWithConditions: (flagName: keyof FeatureFlags, customContext?: UserContext) => boolean;
    getConditions: (flagName: keyof FeatureFlags) => Record<string, any>;
    featureFlags: typeof FEATURE_FLAGS;
    analytics: FeatureFlagAnalytics;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | null>(null);

// Feature flags provider component
export interface FeatureFlagsProviderProps {
    children: React.ReactNode;
    initialUserContext?: UserContext;
}

export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({ children, initialUserContext = {} }) => {
    const [userContext, setUserContext] = React.useState<UserContext>(initialUserContext);
    const analytics = React.useMemo(() => FeatureFlagAnalytics.getInstance(), []);

    const contextValue = useMemo<FeatureFlagsContextValue>(
        () => ({
            userContext,
            setUserContext,
            isEnabled: (flagName) => {
                FeatureFlagPerformanceMonitor.startMeasurement(flagName as string);
                const enabled = isFeatureEnabled(flagName);
                FeatureFlagPerformanceMonitor.endMeasurement(flagName as string);

                // Track usage
                analytics.trackFeatureFlagUsage(flagName, enabled, userContext);

                return enabled;
            },
            isEnabledWithConditions: (flagName, customContext) => {
                FeatureFlagPerformanceMonitor.startMeasurement(flagName as string);
                const context = customContext || userContext;
                const enabled = isFeatureEnabledWithConditions(flagName, context);
                FeatureFlagPerformanceMonitor.endMeasurement(flagName as string);

                // Track usage
                analytics.trackFeatureFlagUsage(flagName, enabled, context);

                return enabled;
            },
            getConditions: (flagName) => FEATURE_FLAGS[flagName]?.conditions || {},
            featureFlags: FEATURE_FLAGS,
            analytics
        }),
        [userContext, analytics]
    );

    return <FeatureFlagsContext.Provider value={contextValue}>{children}</FeatureFlagsContext.Provider>;
};

// Hook to use feature flags
export const useFeatureFlags = (): FeatureFlagsContextValue => {
    const context = useContext(FeatureFlagsContext);
    if (!context) {
        throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
    }
    return context;
};

// Hook to check if a specific feature is enabled
export const useFeatureFlag = (flagName: keyof FeatureFlags): boolean => {
    const { isEnabledWithConditions } = useFeatureFlags();
    return isEnabledWithConditions(flagName);
};

// Hook to get feature flag with conditions
export const useFeatureFlagWithConditions = (flagName: keyof FeatureFlags, customContext?: UserContext): boolean => {
    const { isEnabledWithConditions } = useFeatureFlags();
    return isEnabledWithConditions(flagName, customContext);
};

// Feature flag component for conditional rendering
export interface FeatureGateProps {
    feature: keyof FeatureFlags;
    fallback?: React.ReactNode;
    children: React.ReactNode;
    userContext?: UserContext;
}

export const FeatureGate: React.FC<FeatureGateProps> = ({ feature, fallback = null, children, userContext }) => {
    const { isEnabledWithConditions } = useFeatureFlags();
    const isEnabled = isEnabledWithConditions(feature, userContext);

    return isEnabled ? <>{children}</> : <>{fallback}</>;
};

// Higher-order component for feature gating
export function withFeatureFlag<P extends object>(Component: React.ComponentType<P>, flagName: keyof FeatureFlags, fallback?: React.ComponentType<P>) {
    return function FeatureGatedComponent(props: P) {
        const isEnabled = useFeatureFlag(flagName);

        if (isEnabled) {
            return <Component {...props} />;
        }

        if (fallback) {
            const FallbackComponent = fallback;
            return <FallbackComponent {...props} />;
        }

        return null;
    };
}

// HOC for automatic feature flag usage tracking
export function withFeatureFlagTracking<P extends object>(Component: React.ComponentType<P>, flagName: keyof FeatureFlags) {
    return function TrackedFeatureComponent(props: P) {
        const { isEnabledWithConditions, userContext } = useFeatureFlags();
        const { trackUsage } = useFeatureFlagAnalytics();

        React.useEffect(() => {
            const enabled = isEnabledWithConditions(flagName);
            trackUsage(flagName, enabled, userContext);
        }, [flagName, userContext]);

        return <Component {...props} />;
    };
}
